import {
  useGetQBankStructureLevelsForQuestionReviewQuery,
  type GetQuestionReviewTaskOwnerAssigneeAndTaskByIdQuery,
  type QuestionComment,
  type QuestionVerdict,
  CommentEntityType,
  useGetQuestionCommentsAndVerdictByEntityAndTaskIdQuery,
  useGetQuestionReviewTaskOwnerAssigneeAndTaskByIdQuery,
  useGetTaskQuestionByIdQuery,
  VerdictEntityType,
  type QBankStructureLevel,
  useGetQBankStructureLevelItemsForQuestionReviewQuery,
} from '@exam-management/graphql';
import {
  formatQBankStructureLevelItems,
  type QBankStructureLevelItemWithLevel,
} from '@exam-management/qbs-provider';
import { useAuth } from '@shared/auth';
import { Loading } from '@shared/components';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useEffect, useMemo, useState } from 'react';

import type { Question } from '../../types';

type MainTaskDetails = GetQuestionReviewTaskOwnerAssigneeAndTaskByIdQuery['wflGetTaskById']['task'];

interface QuestionReviewTaskQuestionDetailsProviderProps extends PropsWithChildren {
  taskId: string;
  questionId: string;
}

interface QuestionReviewTaskQuestionDetailsContextData {
  question: Question;
  qBankStructureLevelItems: QBankStructureLevelItemWithLevel[];
  qBankStructureLevels: QBankStructureLevel[];
  mainTaskDetails: MainTaskDetails;
  workflowRoles: {
    isOwner: boolean;
    isAssignee: boolean;
  };
  verdict?: QuestionVerdict;
  comment?: QuestionComment;
}

export const QuestionReviewTaskQuestionDetailsContext =
  createContext<QuestionReviewTaskQuestionDetailsContextData | null>(null);

export default function QuestionReviewTaskQuestionDetailsProvider(
  props: Readonly<QuestionReviewTaskQuestionDetailsProviderProps>,
) {
  const { taskId, questionId, children } = props;
  const [isLoading, setIsLoading] = useState(true);

  const { userProfile } = useAuth();

  const { data: question, loading: isLoadingQuestion } = useGetTaskQuestionByIdQuery({
    variables: {
      qctGetTaskQuestionByIdId: questionId,
      taskId,
    },
  });

  const [qBankStructureLevelItems, setQBankStructureLevelItems] = useState<
    QBankStructureLevelItemWithLevel[]
  >([]);

  const { data: getQBankStructureLevelsResult, loading: isGetQBankStructureLevelsLoading } =
    useGetQBankStructureLevelsForQuestionReviewQuery({
      variables: {
        taskId,
        qBankStructureId: question?.qctGetTaskQuestionById.refQBankStructureId ?? '',
      },
      skip: !question?.qctGetTaskQuestionById.refQBankStructureId,
    });

  const { data: getQBankStructureLevelItemsResult, loading: isGetQBankStructureLevelItemsLoading } =
    useGetQBankStructureLevelItemsForQuestionReviewQuery({
      variables: {
        qBankStructureId: question?.qctGetTaskQuestionById.refQBankStructureId ?? '',
        taskId,
      },
      skip: !question?.qctGetTaskQuestionById.refQBankStructureId,
    });

  const { data: questionVerdictAndComment, loading: isLoadingQuestionVerdictAndComment } =
    useGetQuestionCommentsAndVerdictByEntityAndTaskIdQuery({
      variables: {
        verdictInput: { entityId: questionId, entityType: VerdictEntityType.Question, taskId },
        commentInput: { entityId: questionId, entityType: CommentEntityType.Question, taskId },
      },
    });

  const { data: mainTaskData, loading: isLoadingMainTaskDetails } =
    useGetQuestionReviewTaskOwnerAssigneeAndTaskByIdQuery({
      variables: {
        input: {
          taskId,
        },
      },
    });

  const mainTaskDetails = useMemo(
    () => mainTaskData?.wflGetTaskById.task ?? {},
    [mainTaskData],
  ) as MainTaskDetails;

  const workflowRoles = useMemo(
    () => ({
      isOwner: mainTaskDetails?.ownerId === userProfile?.id,
      isAssignee: mainTaskDetails?.assigneeId === userProfile?.id,
    }),
    [mainTaskDetails, userProfile?.id],
  );

  const questionVerdict = useMemo(
    () =>
      questionVerdictAndComment?.qctGetQuestionVerdictsByEntityAndTaskId.find(
        (verdict) => verdict.userId === userProfile?.id,
      ),
    [questionVerdictAndComment?.qctGetQuestionVerdictsByEntityAndTaskId, userProfile?.id],
  );

  const questionComment = useMemo(
    () =>
      questionVerdictAndComment?.qctGetQuestionCommentsByEntityAndTaskId.find(
        (verdict) => verdict.userId === userProfile?.id,
      ),
    [userProfile?.id, questionVerdictAndComment?.qctGetQuestionCommentsByEntityAndTaskId],
  );

  const questionData = useMemo(
    () => question?.qctGetTaskQuestionById ?? {},
    [question],
  ) as Question;

  const qBankStructureLevelItemsData = useMemo(
    () =>
      getQBankStructureLevelItemsResult?.qbsGetTaskQBankStructureLevelItemsByQBankStructureId ?? [],
    [getQBankStructureLevelItemsResult],
  );

  const qBankStructureLevels = useMemo(
    () =>
      [
        ...(getQBankStructureLevelsResult?.qbsGetTaskQBankStructureLevelsByQBankStructureId ?? []),
      ].sort((a, b) => {
        return a.level - b.level;
      }),
    [getQBankStructureLevelsResult],
  ) as QBankStructureLevel[];

  const isFetching =
    isLoadingQuestion ||
    isGetQBankStructureLevelsLoading ||
    isGetQBankStructureLevelItemsLoading ||
    isLoadingQuestionVerdictAndComment ||
    isLoadingMainTaskDetails;

  useEffect(() => {
    if (!isFetching && qBankStructureLevelItemsData.length) {
      const qBankStructureLevelItemsWithLevel = formatQBankStructureLevelItems(
        qBankStructureLevelItemsData,
        qBankStructureLevels,
      );
      setQBankStructureLevelItems(qBankStructureLevelItemsWithLevel);
      setIsLoading(false);
    }
  }, [qBankStructureLevels, qBankStructureLevelItemsData, isFetching]);

  const contextValues = useMemo(
    () => ({
      question: questionData,
      qBankStructureLevelItems: qBankStructureLevelItems,
      qBankStructureLevels: qBankStructureLevels,
      mainTaskDetails,
      workflowRoles,
      verdict: questionVerdict,
      comment: questionComment,
    }),
    [
      questionData,
      qBankStructureLevelItems,
      qBankStructureLevels,
      questionVerdict,
      questionComment,
      workflowRoles,
      mainTaskDetails,
    ],
  );

  if (isLoading || isFetching) {
    return <Loading height='100%' />;
  }

  return (
    <QuestionReviewTaskQuestionDetailsContext.Provider value={contextValues}>
      {children}
    </QuestionReviewTaskQuestionDetailsContext.Provider>
  );
}
